import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMY, KIND_LABEL } from "@/lib/date";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warning, Clock, CheckCircle, Buildings, ArrowRight, Coins, PencilSimple } from "@phosphor-icons/react";

function KPI({ label, value, sub, icon: Icon, bg, testid, onEdit, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-6 relative transition ${clickable ? "cursor-pointer hover:shadow-md hover:border-[#1E6BC6]/40 hover:-translate-y-0.5" : ""}`}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      {onEdit && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="absolute top-3 right-3 text-[#9CA3AF] hover:text-[#111827]"
                data-testid={`${testid}-edit`}>
          <PencilSimple size={14} />
        </button>
      )}
      <div className="flex items-start gap-4">
        <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${bg}`}>
          {Icon && <Icon size={22} weight="duotone" className="text-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#4B5563]">{label}</div>
          <div className="mt-2 text-4xl font-bold tracking-tight text-[#111827]">{value}</div>
          <div className="mt-1 text-xs text-[#6B7280]">{sub}</div>
        </div>
      </div>
    </div>
  );
}

const fmtMYR = (n) => (n || 0).toLocaleString("en-MY", { style: "currency", currency: "MYR",
  minimumFractionDigits: 2, maximumFractionDigits: 2 });
void fmtMYR;

function AvailableCreditsCard({ onClick }) {
  const { user } = useAuth();
  const [state, setState] = useState({ balance: 0, unlimited: false, used: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/settings/access-tokens").then(r => setState(r.data)).finally(() => setLoading(false));
  }, []);
  // For admin: show global unlimited/company slots. For everyone else: show their personal credits.
  const isAdmin = user?.role === "admin";
  let val, sub;
  if (isAdmin) {
    val = loading ? "—" : state.unlimited ? "Unlimited" : `${Math.max(0, state.balance - state.used)}`;
    sub = state.unlimited ? "Owner — unlimited companies" : `${state.used} used of ${state.balance}`;
  } else {
    const c = Number(user?.credits ?? 0);
    val = `${c}`;
    sub = c === 0 ? "Contact admin to add credits" : "Tax file credits available";
  }
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-6 transition ${clickable ? "cursor-pointer hover:shadow-md hover:border-[#1E6BC6]/40 hover:-translate-y-0.5" : ""}`}
      data-testid="kpi-credit"
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-[#1E6BC6] flex items-center justify-center shrink-0">
          <Coins size={20} weight="duotone" className="text-white" />
        </div>
        <span className="text-sm font-medium text-[#4B5563]">Available Credits</span>
      </div>
      <div className="mt-5 flex items-end justify-between gap-3">
        <div className={`font-bold tracking-tight text-[#111827] ${val === "Unlimited" ? "text-3xl" : "text-4xl"}`}>{val}</div>
        <div className="text-[11px] text-[#9CA3AF] text-right leading-tight max-w-[120px]">{sub}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditInput, setCreditInput] = useState("");
  const [creditSaving, setCreditSaving] = useState(false);

  const refresh = () =>
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/alerts?window=30"),
    ]).then(([s, a]) => {
      setStats(s.data);
      setAlerts(a.data);
    }).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const openCredit = () => {
    setCreditInput(String(stats?.available_credit ?? ""));
    setCreditOpen(true);
  };
  void openCredit;

  const saveCredit = async () => {
    const v = parseFloat(creditInput);
    if (isNaN(v) || v < 0) return toast.error("Enter a valid amount");
    setCreditSaving(true);
    try {
      await api.put("/settings/available-credit", { value: v });
      toast.success("Credit updated");
      setCreditOpen(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setCreditSaving(false); }
  };

  const overdue = alerts.filter(a => a.status === "overdue");
  const soon = alerts.filter(a => a.status !== "overdue" && a.days_until <= 30).slice(0, 30);

  const displayName = (user?.name || (user?.email || "").split("@")[0] || "there").trim();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const dayStr  = now.toLocaleDateString("en-US", { weekday: "long" });
  const timeStr = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <div className="p-8 max-w-[1400px]">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-[#111827]">
          Hello, <span className="text-[#10B981]">{displayName}</span>
        </h1>
        <div className="mt-2 text-sm text-[#6B7280]">
          {dateStr} <span className="mx-2 text-[#D1D5DB]">|</span> {dayStr}, {timeStr}
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KPI label="Total Clients" value={loading ? "—" : stats.total_clients} sub="Active companies"
             icon={Buildings} bg="bg-[#111827]" testid="kpi-total-clients"
             onClick={() => navigate("/clients")} />
        <KPI label="Submitted this year" value={loading ? "—" : stats.submitted_this_year} sub="Filings this year"
             icon={CheckCircle} bg="bg-[#10B981]" testid="kpi-submitted"
             onClick={() => navigate("/dashboard/deadlines?type=submitted")} />
        <KPI label="Due ≤ 30 Days" value={loading ? "—" : stats.upcoming_30_count} sub="Upcoming deadlines"
             icon={Clock} bg="bg-[#F59E0B]" testid="kpi-upcoming"
             onClick={() => navigate("/dashboard/deadlines?type=upcoming")} />
        <KPI label="Overdue" value={loading ? "—" : stats.overdue_count} sub="Requires immediate action"
             icon={Warning} bg="bg-[#3B82F6]" testid="kpi-overdue"
             onClick={() => navigate("/dashboard/deadlines?type=overdue")} />
        <AvailableCreditsCard onClick={() => {
          if (user?.role === "admin") navigate("/dashboard/deadlines?type=credits");
          else navigate("/dashboard/deadlines?type=credit_history");
        }} />
      </section>

      <div className="flex items-center justify-end mb-4">
        <Link to="/clients" className="text-sm text-[#1E6BC6] hover:underline inline-flex items-center gap-1"
              data-testid="dashboard-view-all-clients">
          See All <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {overdue.length > 0 && (
        <section className="mb-6 bg-white rounded-lg shadow-sm border border-[#FCA5A5]">
          <header className="flex items-center justify-between px-6 py-3 border-b border-[#FCA5A5]">
            <div className="flex items-center gap-3">
              <Warning size={18} weight="fill" className="text-[#DA1E28]" />
              <span className="font-semibold text-sm text-[#DA1E28]">Overdue · {overdue.length}</span>
            </div>
          </header>
          <AlertsTable items={overdue} testid="overdue-table" />
        </section>
      )}

      <section className="bg-white rounded-lg shadow-sm border border-[#E5E7EB]">
        <header className="flex items-center justify-between px-6 py-3 border-b border-[#E5E7EB]">
          <span className="font-semibold text-sm text-[#111827]">Upcoming — 30-day countdown</span>
          <span className="text-xs text-[#6B7280]">{soon.length} items</span>
        </header>
        {soon.length === 0 ? (
          <div className="p-10 text-sm text-[#6B7280]">No upcoming deadlines. Add a client to get started.</div>
        ) : (
          <AlertsTable items={soon} testid="upcoming-table" />
        )}
      </section>

      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent className="max-w-sm rounded-lg">
          <DialogHeader><DialogTitle>Update available credit</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Amount (MYR)
            </Label>
            <Input type="number" step="0.01" min="0"
                   value={creditInput} onChange={e => setCreditInput(e.target.value)}
                   className="rounded-md h-11" data-testid="credit-input" />
            <p className="text-[11px] text-muted-foreground">
              Shown as retainer balance on the dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setCreditOpen(false)}>Cancel</Button>
            <Button className="rounded-md bg-[#1E6BC6] hover:bg-[#155A9F]"
                    onClick={saveCredit} disabled={creditSaving} data-testid="credit-save-btn">
              {creditSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function daysBetween(fromIso, toDate) {
  if (!fromIso) return null;
  try {
    const d = new Date(fromIso + (fromIso.length <= 10 ? "T00:00:00Z" : ""));
    if (isNaN(d)) return null;
    const utcTo = Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate());
    const utcFrom = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    return Math.round((utcTo - utcFrom) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function AlertsTable({ items, testid, onNavigate }) {
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="text-left border-b border-border">
          <Th>Client</Th><Th>SSM</Th><Th>Deadline</Th><Th>Anniversary</Th><Th>Due Date</Th>
          <Th>Days</Th><Th>Balance Days to Submit</Th><Th>Status</Th><Th></Th>
        </tr>
      </thead>
      <tbody>
        {items.map((a, i) => {
          const isOver = a.status === "overdue";
          const now = new Date();
          // Days elapsed since the filing window opened (anniversary → today)
          const elapsed = daysBetween(a.anniversary_date, now);
          // Balance days = due − today (server already computed; may be negative)
          const balance = a.days_until;
          return (
            <tr key={i} className={`data-row border-b border-border ${isOver ? "border-l-4 border-l-[#DA1E28]" : ""}`}
                data-testid={`alert-row-${i}`}>
              <td className="px-6 py-3">{a.client_name}</td>
              <td className="px-6 py-3 font-mono text-xs">{a.ssm_no}</td>
              <td className="px-6 py-3">{KIND_LABEL[a.kind]}</td>
              <td className="px-6 py-3 font-mono">{fmtMY(a.anniversary_date)}</td>
              <td className="px-6 py-3 font-mono">{fmtMY(a.due_date)}</td>
              <td className="px-6 py-3 font-mono" data-testid={`days-elapsed-${i}`}>
                {elapsed === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : elapsed < 0 ? (
                  <span className="text-muted-foreground">Not yet</span>
                ) : (
                  <span>{elapsed} days</span>
                )}
              </td>
              <td className="px-6 py-3 font-mono" data-testid={`balance-days-${i}`}>
                {balance < 0 ? (
                  <span className="text-[#DA1E28] font-medium">{Math.abs(balance)} days overdue</span>
                ) : balance === 0 ? (
                  <span className="text-[#DA1E28] font-medium">Due today</span>
                ) : (
                  <span className={balance <= 30 ? "text-[#F1C21B] font-medium" : "text-foreground"}>
                    {balance} days left
                  </span>
                )}
              </td>
              <td className="px-6 py-3"><StatusBadge status={a.status} /></td>
              <td className="px-6 py-3 text-right">
                <Link to={`/clients/${a.client_id}`} onClick={onNavigate}
                      className="text-xs font-mono uppercase tracking-widest underline">
                  Open
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;

